import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Check, Phone, ChevronRight } from "lucide-react";
import { getCityBySlug, CITY_LOCATIONS } from "@/lib/locationData";
import SEOHead from "@/components/SEOHead";
import { BRAND } from "@/lib/data";

function buildPageSchema(loc) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "JobPosting",
        "title": `CDL-A Truck Driver — ${loc.fullName}`,
        "description": loc.description,
        "datePosted": "2026-02-01",
        "validThrough": "2026-12-31",
        "employmentType": ["FULL_TIME"],
        "hiringOrganization": {
          "@type": "Organization",
          "name": "Hylas Companion",
          "sameAs": "https://hylascompanion.com",
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": loc.city,
            "addressRegion": loc.state,
            "addressCountry": "US",
          },
        },
        "baseSalary": {
          "@type": "MonetaryAmount",
          "currency": "USD",
          "value": { "@type": "QuantitativeValue", "minValue": 1200, "maxValue": 2500, "unitText": "WEEK" },
        },
        "qualifications": "Valid CDL-A license required.",
        "applicantLocationRequirements": { "@type": "AdministrativeArea", "name": loc.fullName },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",               "item": "https://hylascompanion.com" },
          { "@type": "ListItem", "position": 2, "name": "CDL Driver Positions","item": "https://hylascompanion.com/positions" },
          { "@type": "ListItem", "position": 3, "name": `${loc.city}, ${loc.state} Driver Jobs`, "item": `https://hylascompanion.com/jobs/${loc.slug}` },
        ],
      },
    ],
  };
}

export default function CityJobPage() {
  const { citySlug } = useParams();
  const loc = getCityBySlug(citySlug);

  if (!loc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <SEOHead title="Page Not Found" description="This page does not exist." path={`/jobs/${citySlug}`} noindex />
        <h1 className="font-head text-3xl font-bold">Page Not Found</h1>
        <Link to="/positions" className="text-primary underline">View All Positions</Link>
      </div>
    );
  }

  return (
    <div data-testid={`city-page-${loc.slug}`}>
      <SEOHead
        title={`CDL-A Truck Driver Jobs in ${loc.fullName}`}
        description={loc.description}
        keywords={loc.keywords}
        path={`/jobs/${loc.slug}`}
        schema={buildPageSchema(loc)}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0A0A] pt-36 pb-20 md:pt-44 md:pb-28 border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="font-head text-xs uppercase tracking-[0.3em] text-primary">
              {loc.metro}
            </span>
          </div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-[0.1em] text-white leading-none">
            CDL-A Driver Jobs<br />
            <span className="text-primary">{loc.fullName}</span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
            {loc.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/apply"
              data-testid={`city-apply-btn-${loc.slug}`}
              className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-8 py-4 hover:bg-white transition-colors duration-300"
            >
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${BRAND.phone}`}
              className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] border-2 border-white/30 text-white px-8 py-4 hover:border-primary hover:text-primary transition-colors duration-300"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Positions Available */}
      <section className="py-20 bg-white border-b border-border/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="font-head text-xs uppercase tracking-[0.3em] text-foreground/40">
            Open Positions
          </span>
          <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] mt-3 mb-8">
            Positions Available in {loc.city}, {loc.state}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {loc.positions.map((pos) => (
              <div
                key={pos}
                className="flex items-center gap-4 p-5 border border-border bg-[#FAFAFA]"
              >
                <div className="w-6 h-6 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="font-head text-sm uppercase tracking-[0.1em] text-foreground">
                  {pos}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Freight Accounts */}
      {loc.accounts && loc.accounts.length > 0 && (
        <section className="py-20 bg-[#0A0A0A] border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-head text-xs uppercase tracking-[0.3em] text-primary/60">
              Dedicated Freight
            </span>
            <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] mt-3 text-white mb-8">
              Accounts We Run in {loc.city}
            </h2>
            <div className="flex flex-wrap gap-3">
              {loc.accounts.map((acct) => (
                <span
                  key={acct}
                  className="font-head text-xs uppercase tracking-[0.2em] px-5 py-2.5 border border-primary/40 text-primary bg-primary/5"
                >
                  {acct}
                </span>
              ))}
            </div>
            <p className="mt-6 text-white/60 max-w-xl leading-relaxed">
              {loc.extraCopy}
            </p>
          </div>
        </section>
      )}

      {/* Why Hylas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="font-head text-xs uppercase tracking-[0.3em] text-foreground/40">
            Why Drive With Us
          </span>
          <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] mt-3 mb-8">
            What Every Driver Gets
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {[
              "New trucks — no old equipment",
              "Weekly direct pay",
              "Flexible home time",
              "No application fees",
              "All 48 states coverage",
              "Dedicated freight accounts",
            ].map((perk) => (
              <div key={perk} className="flex items-start gap-3 p-4 bg-[#FAFAFA] border border-border">
                <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-foreground/80">{perk}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Markets */}
      {loc.nearby && loc.nearby.length > 0 && (
        <section className="py-20 bg-[#0A0A0A]" data-testid="nearby-markets">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-head text-xs uppercase tracking-[0.3em] text-primary/60">
              Explore More Markets
            </span>
            <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.1em] mt-3 text-white mb-8">
              Nearby Hiring Markets
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {loc.nearby.map((slug) => {
                const nearby = getCityBySlug(slug);
                if (!nearby) return null;
                return (
                  <Link
                    key={slug}
                    to={`/jobs/${slug}`}
                    data-testid={`nearby-link-${slug}`}
                    className="group flex flex-col justify-between p-6 border border-white/10 bg-white/[0.03] hover:border-primary/60 hover:bg-primary/5 transition-all duration-300"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-3.5 h-3.5 text-primary/60 shrink-0" />
                        <span className="font-head text-[10px] uppercase tracking-[0.25em] text-primary/60">
                          {nearby.metro}
                        </span>
                      </div>
                      <h3 className="font-head text-lg font-bold uppercase tracking-[0.1em] text-white group-hover:text-primary transition-colors duration-200">
                        {nearby.city}, {nearby.state}
                      </h3>
                      <p className="mt-2 text-white/50 text-sm line-clamp-1">
                        {nearby.positions[0]}
                      </p>
                    </div>
                    <div className="mt-4 flex items-center gap-1 text-primary/60 group-hover:text-primary transition-colors duration-200">
                      <span className="font-head text-xs uppercase tracking-[0.2em]">View Positions</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Driver Transfer Market cross-link */}
      {(() => {
        const STATE_TO_MARKET = {
          IL: "illinois", TX: "texas", FL: "florida", GA: "georgia",
          TN: "tennessee", MS: "mississippi", NC: "north-carolina",
          IN: "indiana", OH: "ohio", CA: "california",
        };
        const mktSlug = STATE_TO_MARKET[loc.state];
        if (!mktSlug) return null;
        const stateName = mktSlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        return (
          <section className="py-14 bg-[#080808] border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <span className="font-head text-[9px] uppercase tracking-[0.4em] text-primary/60">Live Driver Bids</span>
                <h2 className="font-head text-2xl md:text-3xl font-bold uppercase tracking-[0.08em] text-white mt-2">
                  {stateName} Driver Transfer Market
                </h2>
                <p className="mt-3 text-white/50 text-sm max-w-lg">
                  Carriers compete in live placement bids for pre-screened CDL-A drivers based in {stateName}.
                  Browse available drivers, review CDL class and endorsements, and submit a bid — all in one place.
                </p>
              </div>
              <Link
                to={`/market/${mktSlug}`}
                className="shrink-0 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-primary text-black px-8 py-4 hover:brightness-110 transition-all"
              >
                View {stateName} Market <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        );
      })()}

      {/* CTA Banner */}
      <section className="bg-primary py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-black">
              Ready to Drive in {loc.city}?
            </h2>
            <p className="mt-2 text-black/70 text-base">
              Apply in under 2 minutes. No fees. No gimmicks.
            </p>
          </div>
          <Link
            to="/apply"
            className="shrink-0 inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] bg-black text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300"
          >
            Start Your Application <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Breadcrumb nav */}
      <div className="bg-[#0A0A0A] py-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-2 text-xs text-white/30 font-head uppercase tracking-[0.15em]">
          <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/positions" className="hover:text-white/60 transition-colors">Positions</Link>
          <span>/</span>
          <span className="text-white/50">{loc.city}, {loc.state}</span>
        </div>
      </div>
    </div>
  );
}
